/**
 * Background Task Tracker — Google Drive-style floating panel
 * Shows upload progress AND scaffold generation progress
 * Persists across navigation in bottom-right corner
 */
import React, { useState } from 'react';
import { useUpload, UploadItem } from '../../context/UploadContext';
import { useScaffoldGeneration, ScaffoldGenTask } from '../../context/ScaffoldGenerationContext';
import { useQuizGeneration, QuizGenTask } from '../../context/QuizGenerationContext';

const UploadTracker: React.FC = () => {
  const { uploads, dismissUpload, dismissCompleted, hasActiveUploads } = useUpload();
  const { tasks: scaffoldTasks, dismissTask: dismissScaffoldTask, dismissCompleted: dismissCompletedScaffolds, hasActiveGenerations } = useScaffoldGeneration();
  const { quizTasks, dismissQuizTask, dismissCompletedQuizTasks, hasActiveQuizGenerations } = useQuizGeneration();
  const [minimized, setMinimized] = useState(false);

  const totalItems = uploads.length + scaffoldTasks.length + quizTasks.length;
  if (totalItems === 0) return null;

  const hasAnyActive = hasActiveUploads || hasActiveGenerations || hasActiveQuizGenerations;

  // Counts across both uploads and scaffold tasks
  const activeUploadCount = uploads.filter(u => u.status === 'uploading').length;
  const activeScaffoldCount = scaffoldTasks.filter(t => t.status === 'generating').length;
  const activeQuizCount = quizTasks.filter(t => t.status === 'queued' || t.status === 'running').length;
  const totalActiveCount = activeUploadCount + activeScaffoldCount + activeQuizCount;
  const doneCount = uploads.filter(u => u.status === 'done').length + scaffoldTasks.filter(t => t.status === 'done').length + quizTasks.filter(t => t.status === 'done').length;
  const errorCount = uploads.filter(u => u.status === 'error').length + scaffoldTasks.filter(t => t.status === 'error').length + quizTasks.filter(t => t.status === 'failed').length;

  let headerText: string;
  if (totalActiveCount > 0) {
    const parts: string[] = [];
    if (activeUploadCount > 0) parts.push(`${activeUploadCount} upload${activeUploadCount !== 1 ? 's' : ''}`);
    if (activeScaffoldCount > 0) parts.push(`${activeScaffoldCount} generation${activeScaffoldCount !== 1 ? 's' : ''}`);
    if (activeQuizCount > 0) parts.push(`${activeQuizCount} quiz${activeQuizCount !== 1 ? 'zes' : ''}`);
    headerText = `Processing ${parts.join(', ')}`;
  } else if (doneCount > 0 && errorCount === 0) {
    headerText = `${doneCount} task${doneCount !== 1 ? 's' : ''} complete`;
  } else if (errorCount > 0) {
    headerText = `${errorCount} task${errorCount !== 1 ? 's' : ''} failed`;
  } else {
    headerText = 'Background Tasks';
  }

  const handleDismissAll = () => {
    dismissCompleted();
    dismissCompletedScaffolds();
    dismissCompletedQuizTasks();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 bg-white dark:bg-secondary-800 rounded-xl shadow-2xl border border-gray-200 dark:border-secondary-700 overflow-hidden transition-all duration-300">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-secondary-750 border-b border-gray-200 dark:border-secondary-700 cursor-pointer"
        onClick={() => setMinimized(!minimized)}
      >
        <div className="flex items-center gap-2">
          {totalActiveCount > 0 && (
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          )}
          {totalActiveCount === 0 && doneCount > 0 && errorCount === 0 && (
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {totalActiveCount === 0 && errorCount > 0 && (
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {headerText}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title={minimized ? 'Expand' : 'Minimize'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {minimized
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              }
            </svg>
          </button>
          {!hasAnyActive && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDismissAll(); }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Items (hidden when minimized) */}
      {!minimized && (
        <div className="max-h-72 overflow-y-auto">
          {/* Quiz generation jobs */}
          {quizTasks.map((task) => (
            <QuizGenRow key={task.jobId} task={task} onDismiss={dismissQuizTask} />
          ))}
          {/* Scaffold generation tasks */}
          {scaffoldTasks.map((task) => (
            <ScaffoldGenRow key={task.id} task={task} onDismiss={dismissScaffoldTask} />
          ))}
          {/* Upload tasks */}
          {uploads.map((upload) => (
            <UploadRow key={upload.id} upload={upload} onDismiss={dismissUpload} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Quiz Generation Row ── */
const QuizGenRow: React.FC<{ task: QuizGenTask; onDismiss: (jobId: string) => void }> = ({ task, onDismiss }) => {
  const active = task.status === 'queued' || task.status === 'running';
  const progress = task.status === 'done'
    ? 100
    : task.total > 0
      ? Math.round((task.completed / task.total) * 100)
      : 0;

  return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-secondary-700 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            Quiz: {task.title}
          </p>
          {active && (
            <>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                <span>
                  {task.status === 'queued'
                    ? 'Waiting to start...'
                    : `${task.completed} of ${task.total} questions`}
                </span>
                <span className="text-primary-600 dark:text-primary-400 font-medium">{progress}%</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-200 dark:bg-secondary-600 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-500"
                  style={{ width: `${Math.max(progress, 4)}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                You can keep using the app. The quiz appears in Assessments when ready.
              </p>
            </>
          )}
          {task.status === 'done' && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
              Ready with {task.total} questions. Find it under Assessments.
            </p>
          )}
          {task.status === 'failed' && (
            <p className="text-xs text-red-500 mt-0.5 truncate" title={task.error || undefined}>
              {task.error || 'Generation failed. Try again.'}
            </p>
          )}
        </div>
        {!active && (
          <button
            onClick={() => onDismiss(task.jobId)}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Scaffold Generation Row ── */
const ScaffoldGenRow: React.FC<{ task: ScaffoldGenTask; onDismiss: (id: string) => void }> = ({ task, onDismiss }) => {
  const progress = task.totalTopics > 0
    ? Math.round(((task.status === 'done' || task.status === 'error' ? task.totalTopics : task.currentIndex + 0.5) / task.totalTopics) * 100)
    : 0;

  // ETA calculation
  let eta = '';
  if (task.status === 'generating' && task.currentIndex > 0) {
    const elapsed = (Date.now() - task.startTime) / 1000;
    const avgPerScaffold = elapsed / task.currentIndex;
    const remaining = Math.ceil((avgPerScaffold * (task.totalTopics - task.currentIndex)) / 60);
    eta = remaining > 0 ? ` · ~${remaining} min left` : '';
  }

  return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-secondary-700 last:border-b-0">
      <div className="flex items-center gap-3">
        {/* Scaffold icon */}
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14l2-2 2 2 4-4" />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {task.status === 'generating' && (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                Generating: "{task.currentTopic}"
              </p>
              <div className="mt-1.5">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500 dark:text-gray-400">
                    {task.currentIndex + 1} of {task.totalTopics} scaffolds{eta}
                  </span>
                  <span className="text-primary-600 dark:text-primary-400 font-medium">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-1.5">
                  <div
                    className="bg-amber-500 h-1.5 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </>
          )}

          {task.status === 'done' && (
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {task.successCount} scaffold{task.successCount !== 1 ? 's' : ''} generated
              {task.failCount > 0 && (
                <span className="text-red-500 ml-1">({task.failCount} failed)</span>
              )}
            </p>
          )}

          {task.status === 'error' && (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Scaffold generation failed</p>
              <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                All {task.totalTopics} scaffolds failed to generate
              </p>
            </>
          )}
        </div>

        {/* Status icon */}
        <div className="flex-shrink-0">
          {task.status === 'done' && (
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {task.status === 'error' && (
            <button
              onClick={() => onDismiss(task.id)}
              className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {task.status === 'generating' && (
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              {task.currentIndex + 1}/{task.totalTopics}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Upload Row ── */
const UploadRow: React.FC<{ upload: UploadItem; onDismiss: (id: string) => void }> = ({ upload, onDismiss }) => {
  const isLecture = upload.fileType === 'lecture';

  return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-secondary-700 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          {isLecture ? (
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {upload.fileName}
          </p>

          {upload.status === 'uploading' && (
            <div className="mt-1.5">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500 dark:text-gray-400">Uploading...</span>
                <span className="text-primary-600 dark:text-primary-400 font-medium">{upload.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-1.5">
                <div
                  className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            </div>
          )}

          {upload.status === 'error' && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 truncate">
              {upload.error || 'Upload failed'}
            </p>
          )}
        </div>

        <div className="flex-shrink-0">
          {upload.status === 'done' && (
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {upload.status === 'error' && (
            <button
              onClick={() => onDismiss(upload.id)}
              className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {upload.status === 'uploading' && (
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{upload.progress}%</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadTracker;
